import React, { useState, useEffect } from "react";
import { Button, FormInput, PasswordInput } from "../Common.jsx";
import { DynamicIcon } from "../icons.jsx";

const IdentityOption = ({ id, activeId, icon, title, desc, onClick }) => (
    <button
        onClick={() => onClick(id)}
        className={`flex-1 p-4 rounded-xl border-2 text-left transition-all relative overflow-hidden group ${activeId === id ? "border-primary bg-primary/5" : "border-gray-200 hover:border-gray-300 dark:border-gray-700"}`}
    >
        <div className={`mb-3 w-10 h-10 rounded-lg flex items-center justify-center ${activeId === id ? "bg-primary text-white" : "bg-gray-100 text-gray-500"}`}>
            <DynamicIcon name={icon} className="w-6 h-6" />
        </div>
        <div className="font-bold text-gray-900 dark:text-gray-100">{title}</div>
        <div className="text-xs text-gray-500 mt-1">{desc}</div>
        {activeId === id && <div className="absolute top-0 right-0 p-1 bg-primary text-white rounded-bl-lg"><DynamicIcon name="Check" className="w-4 h-4" /></div>}
    </button>
);

export default function AuthConfigForm({ data, features, onSave, onNext }) {
    // Merged state for local handling
    const [form, setForm] = useState({
        auth_type: data?.auth_type ?? "email_password",
        email_provider_type: data?.email_provider_type ?? "platform_default",

        // Features
        single_session: features?.enabled_features?.single_session !== false,

        // Password
        password_min_length: data?.password_min_length ?? 8,
        password_requires_uppercase: data?.password_requires_uppercase ?? true,
        password_requires_lowercase: data?.password_requires_lowercase ?? true,
        password_requires_special: data?.password_requires_special ?? false,
        password_requires_number: data?.password_requires_number ?? true,
        password_expiry_days: data?.password_expiry_days ?? 90,

        // Magic Link
        magic_link_expiry_minutes: data?.magic_link_expiry_minutes ?? 5,

        // Session Expiry
        session_expiry_minutes: data?.session_expiry_minutes ?? 15,

        // SMTP
        smtp_host: data?.smtp_host ?? "",
        smtp_port: data?.smtp_port ?? "",
        smtp_user: data?.smtp_user ?? "",
        smtp_password: data?.smtp_password ?? "",
    });

    useEffect(() => {
        setForm(prev => ({
            ...prev,
            ...data, // Hydrate from prop if changed
            // defaults fallback
            email_provider_type: data?.email_provider_type ?? "platform_default",
            single_session: features?.enabled_features?.single_session !== false
        }));
    }, [data, features]);

    const update = (key, value) => {
        setForm({ ...form, [key]: value });
    };

    return (
        <div className="max-w-4xl space-y-8">

            {/* 1. Identity Strategy Selection */}
            <section>
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <DynamicIcon name="UserCheck" className="text-primary" /> Primary Identity Strategy
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <IdentityOption
                        id="email_password"
                        activeId={form.auth_type}
                        icon="Mail"
                        title="Email + Password"
                        desc="Standard login with password policies"
                        onClick={(id) => update("auth_type", id)}
                    />
                    <IdentityOption
                        id="magic_link"
                        activeId={form.auth_type}
                        icon="Zap"
                        title="Magic Link"
                        desc="Passwordless email login"
                        onClick={(id) => update("auth_type", id)}
                    />
                </div>
            </section>

            {/* 2. Configuration Cards based on Selection */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 p-6 shadow-sm animate-fade-in">

                {/* --- EMAIL + PASSWORD CONFIG --- */}
                {form.auth_type === "email_password" && (
                    <div className="space-y-6">

                        {/* Email Provider Toggle */}
                        <div className="flex flex-col gap-2 border-b pb-6 dark:border-gray-700">
                            <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Email Delivery Provider</label>
                            <div className="flex gap-4">
                                <label className={`flex-1 p-3 rounded border cursor-pointer flex items-center gap-3 ${form.email_provider_type === "platform_default" ? "border-primary bg-primary/5" : "border-gray-200"}`}>
                                    <input
                                        type="radio"
                                        name="email_provider"
                                        checked={form.email_provider_type === "platform_default"}
                                        onChange={() => update("email_provider_type", "platform_default")}
                                    />
                                    <div>
                                        <div className="font-semibold text-sm">Use Aidas Platform Server</div>
                                        <div className="text-xs text-gray-500">Managed delivery, no setup required.</div>
                                    </div>
                                </label>
                                <label className={`flex-1 p-3 rounded border cursor-pointer flex items-center gap-3 ${form.email_provider_type === "custom_smtp" ? "border-primary bg-primary/5" : "border-gray-200"}`}>
                                    <input
                                        type="radio"
                                        name="email_provider"
                                        checked={form.email_provider_type === "custom_smtp"}
                                        onChange={() => update("email_provider_type", "custom_smtp")}
                                    />
                                    <div>
                                        <div className="font-semibold text-sm">Custom SMTP Server</div>
                                        <div className="text-xs text-gray-500">Connect tenant's own email gateway.</div>
                                    </div>
                                </label>
                            </div>

                            {form.email_provider_type === "custom_smtp" && (
                                <div className="mt-4 grid grid-cols-2 gap-4 bg-gray-50 dark:bg-gray-900 p-4 rounded-lg animate-fade-in-down">
                                    <FormInput label="SMTP Host" value={form.smtp_host} onChange={(e) => update("smtp_host", e.target.value)} />
                                    <FormInput label="SMTP Port" type="number" value={form.smtp_port} onChange={(e) => update("smtp_port", e.target.value)} />
                                    <FormInput label="Username" value={form.smtp_user} onChange={(e) => update("smtp_user", e.target.value)} />
                                    <PasswordInput label="Password" value={form.smtp_password} onChange={(e) => update("smtp_password", e.target.value)} />
                                </div>
                            )}
                        </div>

                        {/* Password Policy */}
                        <div>
                            <h4 className="font-bold text-sm text-gray-700 dark:text-gray-300 mb-3">Password Security Policy</h4>
                            <div className="grid grid-cols-2 gap-4">
                                <FormInput label="Min Length" type="number" value={form.password_min_length} onChange={(e) => update("password_min_length", e.target.value)} />
                                <FormInput label="Expiry (Days)" type="number" value={form.password_expiry_days} onChange={(e) => update("password_expiry_days", e.target.value)} />
                            </div>
                            <div className="flex gap-6 pt-4 flex-wrap">
                                <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.password_requires_uppercase} onChange={(e) => update("password_requires_uppercase", e.target.checked)} /> Uppercase</label>
                                <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.password_requires_lowercase} onChange={(e) => update("password_requires_lowercase", e.target.checked)} /> Lowercase</label>
                                <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.password_requires_number} onChange={(e) => update("password_requires_number", e.target.checked)} /> Number</label>
                                <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.password_requires_special} onChange={(e) => update("password_requires_special", e.target.checked)} /> Special Char</label>
                            </div>
                        </div>

                        {/* Session Security */}
                        <div>
                            <h4 className="font-bold text-sm text-gray-700 dark:text-gray-300 mb-3">Session Security</h4>
                            <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700/50 cursor-pointer transition-colors">
                                <input
                                    type="checkbox"
                                    checked={form.single_session}
                                    onChange={(e) => update("single_session", e.target.checked)}
                                    className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
                                />
                                <div>
                                    <div className="font-medium text-gray-900 dark:text-gray-100">Enforce Single Session</div>
                                    <div className="text-xs text-gray-500">Automatically invalidate previous active sessions when logging in from a new device.</div>
                                </div>
                            </label>

                            <div className="mt-4 w-1/2">
                                <FormInput
                                    label="Session Expiry (Minutes)"
                                    type="number"
                                    value={form.session_expiry_minutes}
                                    onChange={(e) => update("session_expiry_minutes", e.target.value)}
                                    helperText="How long a user session stays valid before requiring re-login."
                                />
                            </div>
                        </div>
                    </div>
                )}


                {/* --- MAGIC LINK CONFIG --- */}
                {form.auth_type === "magic_link" && (
                    <div className="space-y-4">
                        <div className="flex items-center gap-4 p-4 bg-purple-50 text-purple-900 rounded-lg">
                            <DynamicIcon name="Sparkles" />
                            <div>
                                <div className="font-bold">Passwordless Experience</div>
                                <div className="text-xs">Users will receive a secure, time-limited link via email to log in.</div>
                            </div>
                        </div>
                        <div className="w-1/2">
                            <FormInput label="Link Expiry (Minutes)" type="number" value={form.magic_link_expiry_minutes} onChange={(e) => update("magic_link_expiry_minutes", e.target.value)} />
                        </div>
                    </div>
                )}
            </div>

            <div className="sticky bottom-0 z-10 bg-white/90 dark:bg-gray-800/90 backdrop-blur border-t dark:border-gray-700 p-4 -mx-6 -mb-6 mt-8 flex justify-end shadow-lg rounded-b-xl">
                <Button
                    onClick={async () => {
                        // Sanitize integers: empty strings should be null, otherwise parseInt
                        const payload = { ...form };
                        const intFields = ['smtp_port', 'password_min_length', 'password_expiry_days', 'magic_link_expiry_minutes', 'session_expiry_minutes'];

                        intFields.forEach(field => {
                            if (payload[field] === "" || payload[field] === null || payload[field] === undefined) {
                                payload[field] = null;
                            } else {
                                const parsed = parseInt(payload[field], 10);
                                payload[field] = isNaN(parsed) ? null : parsed;
                            }
                        });

                        // Fallback defaults if null is not allowed for some fields
                        if (!payload.password_min_length) payload.password_min_length = 8;
                        if (!payload.password_expiry_days) payload.password_expiry_days = 90;

                        await onSave(payload);
                        if (onNext) onNext();
                    }}
                    className="px-6 shadow-md"
                >
                    Save & Next
                    <DynamicIcon name="ArrowRight" className="ml-2 w-4 h4" />
                </Button>
            </div>

        </div>
    );
}
